import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login, resetAuthError } from '../../store/slices/authSlice';
import { RootState } from '../../store';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { refreshUserInfo } from '../../hooks/useUserInfo';
import { redirectAfterLogin } from '../../utils/redirectUtils';
import { ReactComponent as PasswordEyeIcon } from '../../assets/password_eye.svg';
import { ReactComponent as LoginCheckIcon } from '../../assets/login-check.svg';

interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [formValues, setFormValues] = useState<LoginFormValues>({
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<LoginFormValues>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 로그인 성공 시 처리 (실제 로그인 액션 성공 시에만)
  const [previousAuthState, setPreviousAuthState] = useState(false);
  
  useEffect(() => {
    // 이전에 로그인되지 않았었는데 지금 로그인된 경우에만 성공 처리
    if (!previousAuthState && isAuthenticated) {
      // /// console.log('LoginModal - Login success detected, refreshing user info');
      
      // 즉시 사용자 정보 새로고침
      refreshUserInfo().then(() => {
        // /// console.log('LoginModal - User info refreshed successfully');
        
        // 헤더 강제 업데이트를 위해 커스텀 이벤트 발생
        window.dispatchEvent(new CustomEvent('userInfoUpdated'));
        
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        
        onClose();
      }).catch((error) => {
        /// console.error('LoginModal - Failed to refresh user info:', error);
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        onClose();
      });
    }
    setPreviousAuthState(isAuthenticated);
  }, [isAuthenticated, navigate, onLoginSuccess, onClose, previousAuthState]);

  // Redux 에러 처리
  useEffect(() => {
    if (error) {
      setLoginError(error);
    }
  }, [error]);

  // 모달이 열릴 때마다 폼 초기화
  useEffect(() => {
    if (isOpen) {
      setFormValues({ email: '', password: '' });
      setFormErrors({});
      setLoginError('');
      setShowPassword(false);
      setKeepLoggedIn(false);
      dispatch(resetAuthError());
    }
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
    
    // 입력 시 해당 필드 에러 제거
    if (formErrors[name as keyof LoginFormValues]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
    
    // 입력 시 로그인 에러 제거
    if (loginError) {
      setLoginError('');
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<LoginFormValues> = {};
    let isValid = true;

    if (!formValues.email) {
      errors.email = '이메일을 입력해주세요';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = '유효한 이메일 주소를 입력해주세요';
      isValid = false;
    }

    if (!formValues.password) {
      errors.password = '비밀번호를 입력해주세요';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setLoginError('');
      dispatch(login({ 
        email: formValues.email, 
        password: formValues.password,
        rememberMe: keepLoggedIn
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleKeepLoggedIn = () => {
    setKeepLoggedIn(!keepLoggedIn);
  };

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <ModalHeader>
          {/* <ModalTitle> </ModalTitle> */}
          <CloseButton onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#171719"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ModalHeader>

        {/* 슬로건 */}
        <SloganSection>
          <SloganText>대한민국의학한림원 × 투비닥터</SloganText>
          <SloganTitle>MEDICAL AI CAMPUS.</SloganTitle>
        </SloganSection>

        {/* 로그인 폼 */}
        <ModalContent>
          <form onSubmit={handleSubmit}>
            {/* 이메일 입력 */}
            <FormGroup hasBottomMargin>
              <FormLabel>이메일</FormLabel>
              <EmailInput
                type="email"
                name="email"
                placeholder="이메일을 입력해주세요."
                value={formValues.email}
                onChange={handleChange}
                className={formErrors.email ? 'error' : ''}
              />
              {formErrors.email && <InputHelp error>{formErrors.email}</InputHelp>}
            </FormGroup>
            
            {/* 비밀번호 입력 */}
            <FormGroup>
              <FormLabel>비밀번호</FormLabel>
              <PasswordInputWrapper>
                <PasswordInput
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="비밀번호를 입력해주세요."
                  value={formValues.password}
                  onChange={handleChange}
                  className={formErrors.password ? 'error' : ''}
                />
                <EyeIconWrapper onClick={togglePasswordVisibility}>
                  <PasswordEyeIcon width={22} height={22} />
                </EyeIconWrapper>
              </PasswordInputWrapper>
              {formErrors.password && <InputHelp error>{formErrors.password}</InputHelp>}
            </FormGroup>
            
            {/* 로그인 유지 체크박스 */}
            <KeepLoggedInWrapper>
              <CheckboxWrapper onClick={toggleKeepLoggedIn}>
                <CheckboxCustom checked={keepLoggedIn}>
                  <LoginCheckIcon style={{opacity: keepLoggedIn ? 1 : 0}} />
                </CheckboxCustom>
              </CheckboxWrapper>
              <CheckboxLabel onClick={toggleKeepLoggedIn}>
                로그인 유지
              </CheckboxLabel>
            </KeepLoggedInWrapper>
            
            {/* 로그인 에러 메시지 */}
            {loginError && (
              <LoginErrorMessage>
                {loginError}
              </LoginErrorMessage>
            )}
            
            {/* 로그인 버튼 */}
            <LoginButton 
              type="submit" 
              disabled={loading}
            >
              {loading ? '처리 중...' : '로그인'}
            </LoginButton>
            
            {/* 링크 버튼들 */}
            <LinkButtonsRow>
              <LinkButton onClick={onClose}>회원가입</LinkButton>
              <LinkButton onClick={onClose}>비밀번호 찾기</LinkButton>
            </LinkButtonsRow>
          </form>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// 스타일 컴포넌트들
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(23, 23, 25, 0.52);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContainer = styled.div`
  width: 640px;
  max-width: 1060px;
  min-width: 320px;
  max-height: 760px;
  background: white;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  position: relative;
  backdrop-filter: blur(32px);
  padding: 26px 28px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: #171719;
  font-size: 17px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 28px;
  top: 26px;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const SloganSection = styled.div`
  padding: 24px 32px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const SloganText = styled.p`
  color: #171719;
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 30.01px;
  margin: 0;
`;

const SloganTitle = styled.h1`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  margin: 0;
`;

const ModalContent = styled.div`
  padding: 24px 32px 72px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div<{ hasBottomMargin?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-bottom: ${props => props.hasBottomMargin ? '32px' : '0'};
`;

const FormLabel = styled.label`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &.error {
    border-color: #ff5252;
  }
  
  &:focus {
    border-color: rgba(112, 115, 124, 0.16);
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 12px;
  padding-right: 44px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &.error {
    border-color: #ff5252;
  }
  
  &:focus {
    border-color: rgba(112, 115, 124, 0.16);
  }
`;

const EyeIconWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
  
  svg {
    pointer-events: none;
  }
`;

const KeepLoggedInWrapper = styled.div`
  width: 335px;
  height: 20px;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  display: inline-flex;
  margin-top: 8px;
  margin-bottom: 0;
  flex-shrink: 0;
  position: relative;
`;

const CheckboxWrapper = styled.div`
  width: 20px;
  height: 20px;
  padding: 2px;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: inline-flex;
  cursor: pointer;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const CheckboxCustom = styled.div<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  border: 1.5px solid rgba(112, 115, 124, 0.22);
  background-color: ${props => props.checked ? '#448181' : 'transparent'};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  
  ${props => props.checked && `
    border-color: #448181;
  `}
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: none;
  }
`;

const CheckboxLabel = styled.div`
  flex: 1 1 0;
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  cursor: pointer;
  user-select: none;
  align-self: flex-start;
`;

const LoginErrorMessage = styled.div`
  color: #FF4242;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  margin-top: 16px;
  margin-bottom: 0;
`;

const InputHelp = styled.p<{ error?: boolean }>`
  font-size: 12px;
  line-height: 1.334em;
  color: ${props => props.error ? '#ff5252' : 'rgba(55, 56, 60, 0.61)'};
  margin-top: 4px;
  margin-bottom: 0;
`;

const LoginButton = styled.button`
  width: 100%;
  background-color: #448181;
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  padding: 12px 28px;
  margin-top: 24px;
  margin-bottom: 8px;
  min-height: 48px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  
  &:disabled {
    background-color: #F4F4F5;
    color: rgba(55, 56, 60, 0.28);
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: rgba(68, 129, 129, 0.9);
  }
`;

const LinkButtonsRow = styled.div`
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  justify-content: center;
  gap: 48px;
`;

const LinkButton = styled.button`
  color: #448181;
  background: none;
  border: none;
  text-decoration: none;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(68, 129, 129, 0.1);
  }
`;

export default LoginModal;
